import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { UserProfile } from "@/types/user";
import { User } from "lucide-react";
import { useState, useEffect } from "react";
import { useUser } from "@/contexts/UserContext";
import { fetchTeamMembersPulse, UserProfilePulse } from "@/components/team/services/teamService";

type TeamPulseCardProps = {
  teamMembers: UserProfile[];
};

// Create a proper TeamMember component for the pulse card
const TeamMemberItem = ({
  name,
  role,
  status,
  imageSrc,
  lastCheckIn,
  checkInStreak,
}: {
  name: string;
  role: string;
  status: "great" | "good" | "okay" | "low" | "struggling" | "unknown";
  imageSrc?: string;
  lastCheckIn?: string;
  checkInStreak?: number;
}) => {
  const getStatusIndicator = (
    status: "great" | "good" | "okay" | "low" | "struggling" | "unknown"
  ) => {
    const baseClasses = "w-3 h-3 rounded-full";

    const statusClasses = {
      great: "bg-green-400",
      good: "bg-emerald-400",
      okay: "bg-blue-400",
      low: "bg-amber-400",
      struggling: "bg-red-400",
      unknown: "bg-gray-400",
    };

    return <div className={`${baseClasses} ${statusClasses[status]}`}></div>;
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case "great": return "Feeling great";
      case "good": return "Doing well";
      case "okay": return "Okay";
      case "low": return "Low energy";
      case "struggling": return "Struggling";
      case "unknown": return "No recent check-in";
      default: return "Unknown";
    }
  };

  return (
    <div className="flex items-center gap-3 p-3 hover:bg-muted/50 rounded-lg transition-colors">
      <div className="relative">
        {imageSrc ? (
          <img
            src={imageSrc}
            alt={name}
            className="w-10 h-10 rounded-full object-cover"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-muted-foreground">
            <User size={20} />
          </div>
        )}
        <div className="absolute -bottom-1 -right-1">
          {getStatusIndicator(status)}
        </div>
      </div>
      <div className="flex-1">
        <div className="flex items-center justify-between">
          <p className="font-medium text-sm">{name}</p>
          {checkInStreak !== undefined && checkInStreak > 0 && (
            <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
              {checkInStreak} day{checkInStreak > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <p className="text-xs text-muted-foreground">{getStatusText(status)}</p>
        {lastCheckIn && (
          <p className="text-xs text-muted-foreground opacity-75">
            Last check-in: {lastCheckIn}
          </p>
        )}
      </div>
    </div>
  );
};

const TeamPulseCard = ({ teamMembers }: TeamPulseCardProps) => {
  const [teamPulseData, setTeamPulseData] = useState<UserProfilePulse[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useUser();

  useEffect(() => {
    const loadTeamPulse = async () => {
      if (!user?.teamId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const pulseData = await fetchTeamMembersPulse(user.teamId);
        setTeamPulseData(pulseData);
      } catch (error) {
        console.error("Error loading team pulse data:", error);
        setTeamPulseData([]);
      } finally {
        setLoading(false);
      }
    };

    loadTeamPulse();
  }, [user?.teamId]);

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg">Team Pulse</CardTitle>
        <CardDescription>Real-time team mood and engagement</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-1">
          {loading ? (
            // Loading skeleton
            [...Array(3)].map((_, i) => (
              <div key={i} className="flex items-center gap-3 p-3">
                <div className="w-10 h-10 rounded-full bg-gray-200 animate-pulse"></div>
                <div className="flex-1">
                  <div className="h-4 bg-gray-200 rounded animate-pulse mb-1"></div>
                  <div className="h-3 bg-gray-200 rounded animate-pulse w-2/3"></div>
                </div>
              </div>
            ))
          ) : teamPulseData.length === 0 ? (
            <div className="text-center py-4 text-sm text-muted-foreground">
              No team data available
            </div>
          ) : (
            teamPulseData.slice(0, 4).map((member) => (
              <TeamMemberItem
                key={member.id}
                name={member.name}
                role={member.role}
                status={member.status as "great" | "good" | "okay" | "low" | "struggling" | "unknown"}
                imageSrc={member.avatarUrl}
                lastCheckIn={member.lastCheckIn}
                checkInStreak={member.checkInStreak}
              />
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default TeamPulseCard;
